import * as React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import Details from "Catalog/Edit/shared/components/Details"
import EditLayout from "Catalog/Edit/shared/components/EditLayout"
import useDelayedRenderedItem from "Catalog/Edit/shared/hooks/useDelayedRenderedItem"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { OptionListId } from "shared/hubrise/ids"
import { IOptionList } from "shared/hubrise/models"
import * as OptionList from "shared/hubrise/utils/OptionList"

import Content from "./Content"
import Navigator from "./Navigator"
import Toolbar from "./Toolbar"

const OptionLists = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ optionListId: string }>()

  const { catalog } = useEditor()
  const items = catalog.data.optionLists

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.editPath("option_lists")

  const selectedItem = useFindItemOrRedirect<IOptionList>(
    params.optionListId,
    (id) => OptionList.byId(catalog, OptionListId(id)),
    indexPath,
  )

  const renderedItem = useDelayedRenderedItem(selectedItem)

  React.useEffect(() => {
    visit("optionList", selectedItem)
  }, [selectedItem, visit])

  useKeyboardNavigation<IOptionList>({
    item: selectedItem,
    collection: catalog.data.optionLists,
    indexPath: () => indexPath,
    showPath: (optionList) => catalogNavigation.editPath("option_list", optionList.id),
  })

  return (
    <EditLayout
      toolbar={<Toolbar optionList={renderedItem} />}
      navigator={items.length > 0 ? <Navigator items={items} selectedId={selectedItem?.id} /> : null}
      details={
        renderedItem ? (
          <Details.Main testId="catalog-details" resourceId={renderedItem.id}>
            <Content optionList={renderedItem} />
          </Details.Main>
        ) : null
      }
      none={t("option_list.none")}
      noneSelected={t("option_list.none_selected")}
      testId="catalog-option-lists"
    />
  )
}

export default OptionLists
